import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Input, ActionBar, Loader } from 'shared-components';
import { post } from 'shared-components/src/utils/http';
import { numberDollarsToCents } from 'shared-components/src/utils/currency';
import { etransferPushComponentName } from '../utils';
import InputGroup from '../../../../../layouts/InputGroup';
import InputRow from '../../../../../layouts/InputRow';
import Form from '../../../../../layouts/Form';
import SubmitButton from '../../../../../components/SubmitButton';
import EditableDropdown from '../../../components/EditableDropDown/EditableDropDown';
import SecurityQuestionField from '../../../../../components/SecurityQuestionField';
import {
  validateSecurityQuestion,
  securityQuestionErrorMessage,
} from '../../../../../utils/securityQuestion';

const ExternalTransferEtransferPushForm = ({
  disableSubmit,
  externalAccounts,
  setPushParams,
  setShowConfirmModal,
  page,
  deleteExternalAccount,
  editExternalAccount,
  setAlert,
}) => {
  const intl = useIntl();
  const securityAnswerRef = useRef(null);
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityQuestionError, setSecurityQuestionError] = useState(null);

  const [amountInput, setAmountInput] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [aliasReferenceID, setAliasReferenceID] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAliasRegular = paymentType === 'ALIAS_REGULAR';

  const handleSubmit = e => {
    e.preventDefault();
    setAlert(false);

    if (!(selectedOption && selectedOption.id)) {
      setAlert({
        type: 'danger',
        message: intl.messages[`${page}-select-contact-alert`],
        messageFor: 'selection-error',
        component: etransferPushComponentName,
      });
      return;
    }

    if (isAliasRegular) {
      const questionError = validateSecurityQuestion(
        securityQuestion,
        securityAnswerRef.current && securityAnswerRef.current.value
      );
      setSecurityQuestionError(questionError);
      if (questionError) return;
    }

    setPushParams({
      amount: numberDollarsToCents(amountInput),
      transfer_type: 'direct_send_etransfer_push',
      external_account_id: parseInt(selectedOption.id, 10),
      security_question: isAliasRegular ? securityQuestion.trim() : '',
      security_question_answer: (securityAnswerRef.current && securityAnswerRef.current.value) || '',
      payment_type: paymentType,
      alias_reference_id: aliasReferenceID,
    });
    setShowConfirmModal(true);
  };

  const checkPaymentType = async id => {
    setAlert(false);
    setLoading(true);
    try {
      const data = await post(
        `/api/v2/cardholder_portal/cardholder/external_accounts/${id}/check_payment_type`,
        {}
      );


      if (data.payment_type === 'ALIAS_AUTODEPOSIT') {
        setAlert({
          type: 'warn',
          message: intl.messages[`${page}-autodeposit-enabled-alert`],
          messageFor: 'autodeposit-enabled',
          component: etransferPushComponentName,
        });
      }
      
      setPaymentType(data.payment_type);
      setAliasReferenceID(data.alias_reference_id);

      setLoading(false);
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'check-payment-type-error',
        component: etransferPushComponentName,
      });
      setLoading(false);
    }
  };

  const handleSelect = async option => {
    setSelectedOption(option);
    checkPaymentType(option.id);
  };

  if (loading) return <Loader />;

  return (
    <Form onSubmit={handleSubmit}>
      <InputRow>
        <InputGroup
          label={intl.messages[`${page}-form-select-external-account-label`]}
          labelFor="externalAccounts"
          page={page}
          required
        >
          <EditableDropdown
            options={externalAccounts}
            selectedOption={selectedOption}
            onSelect={handleSelect}
            onEdit={editExternalAccount}
            onDelete={deleteExternalAccount}
            page={page}
          />
        </InputGroup>
        <InputGroup
          required
          label={intl.messages[`${page}-amount-label`]}
          labelFor="amount"
          page={page}
        >
          <Input
            name="amount"
            pattern="^\d+(\.\d{1,2})?$"
            title="Please enter a valid amount (minimum 1.00, up to 2 decimal places)"
            placeholder="00.00"
            type="number"
            step="0.01"
            min="1.00"
            value={amountInput}
            onChange={e => setAmountInput(e.target.value)}
            required
          />
        </InputGroup>
      </InputRow>
      {isAliasRegular && (
        <InputRow $alignTop>
          <InputGroup
            required
            label={intl.messages[`${page}-security-question-label`]}
            labelFor="security-question"
            page={page}
          >
            <SecurityQuestionField
              id="security-question"
              dropdownComponent={EditableDropdown}
              value={securityQuestion}
              onChange={question => {
                setSecurityQuestion(question);
                setSecurityQuestionError(null);
              }}
              onBlur={() => setSecurityQuestionError(
                validateSecurityQuestion(
                  securityQuestion,
                  securityAnswerRef.current && securityAnswerRef.current.value
                )
              )}
              error={securityQuestionErrorMessage(intl, securityQuestionError)}
              invalidProps={securityQuestionError ? { className: 'is-invalid' } : {}}
            />
          </InputGroup>
          <InputGroup
            required
            label={intl.messages[`${page}-security-answer-label`]}
            labelFor="security-answer"
            page={page}
          >
            <Input
              id="security-answer-input"
              type="text"
              ref={securityAnswerRef}
              title="Security answer must be 3-25 characters, using only letters and numbers (no spaces)."
              pattern="^[0-9A-Za-z]{3,25}$"
              required
            />
          </InputGroup>
        </InputRow>
      )}
      <ActionBar>
        <SubmitButton disabled={disableSubmit} />
      </ActionBar>
    </Form>
  );
};

ExternalTransferEtransferPushForm.propTypes = {
  externalAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      vanity_identifier: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      updated_at: PropTypes.string.isRequired,
    })
  ).isRequired,
  disableSubmit: PropTypes.bool.isRequired,
  setPushParams: PropTypes.func.isRequired,
  setShowConfirmModal: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired,
  deleteExternalAccount: PropTypes.func.isRequired,
  editExternalAccount: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
};

export default ExternalTransferEtransferPushForm;
